import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { FiFilter, FiSearch, FiX } from "react-icons/fi";
import "./ColumnFilter.css";

const ColumnFilter = ({ column, data, onFilterChange, currentFilters = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedValues, setSelectedValues] = useState(new Set(currentFilters));
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const filterRef = useRef(null);
  const buttonRef = useRef(null);

  // Sync selectedValues when currentFilters changes
  useEffect(() => {
    setSelectedValues(new Set(currentFilters));
  }, [currentFilters]);

  // Get unique values from the column
  const uniqueValues = React.useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }
    
    const values = new Set();
    const accessor = column.accessor;
    const columnId = column.id;
    
    data.forEach((row) => {
      let value;
      
      // Handle accessor as function
      if (typeof accessor === 'function') {
        value = accessor(row);
      } else if (accessor) {
        // Handle accessor as string
        value = row[accessor];
      } else if (columnId) {
        // Fallback to column id
        value = row[columnId];
      }
      
      if (value !== null && value !== undefined && value !== "") {
        values.add(String(value));
      }
    });
    
    const uniqueArray = Array.from(values);
    
    return uniqueArray.sort((a, b) => {
      // Try numeric sort first
      const numA = parseFloat(a);
      const numB = parseFloat(b);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      // Fall back to string sort
      return a.localeCompare(b);
    });
  }, [data, column]);

  // Filter unique values based on search term
  const filteredValues = uniqueValues.filter(value =>
    value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      if (isMobile) {
        // On mobile, don't calculate position - will use fixed bottom positioning
        setDropdownPosition({ top: 0, left: 0 });
      } else {
        // On desktop, position below the button
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX
        });
      }
    }
  }, [isOpen, isMobile]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleToggleValue = (value) => {
    const newSelected = new Set(selectedValues);
    if (newSelected.has(value)) {
      newSelected.delete(value);
    } else {
      newSelected.add(value);
    }
    setSelectedValues(newSelected);
  };

  const handleSelectAll = () => {
    setSelectedValues(new Set(filteredValues));
  };

  const handleClearAll = () => {
    setSelectedValues(new Set());
  };

  const handleApply = () => {
    const valuesArray = Array.from(selectedValues);
    const columnKey = column.id || column.accessor;
    console.log('[ColumnFilter] Applying filter for', columnKey, 'with values:', valuesArray);
    onFilterChange(columnKey, valuesArray);
    setIsOpen(false);
  };

  const handleClear = () => {
    const columnKey = column.id || column.accessor;
    setSelectedValues(new Set());
    onFilterChange(columnKey, []);
    setIsOpen(false);
  };

  const hasActiveFilter = currentFilters.length > 0;

  const renderDropdown = () => {
    if (!isOpen) return null;

    const mobileStyles = isMobile ? {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      top: 'auto',
      borderRadius: '16px 16px 0 0',
      maxHeight: '80vh',
      width: '100%',
      maxWidth: '100%',
      animation: 'slideUp 0.3s ease-out'
    } : {
      position: 'absolute',
      top: `${dropdownPosition.top}px`,
      left: `${dropdownPosition.left}px`
    };

    return ReactDOM.createPortal(
      <>
        {isMobile && <div className="filter-overlay" onClick={() => setIsOpen(false)} />}
        <div 
          className={`filter-dropdown ${isMobile ? 'mobile' : ''}`}
          ref={filterRef}
          style={{
            ...mobileStyles,
            zIndex: 10000
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {isMobile && <div className="filter-drag-handle" />}
          <div className="filter-header">
            <h4>Filter by {column.Header}</h4>
            <button
              className="close-filter"
              onClick={() => setIsOpen(false)}
            >
              <FiX size={16} />
            </button>
          </div>

          <div className="filter-search">
            <FiSearch size={14} />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="filter-actions">
            <button onClick={handleSelectAll} className="filter-action-btn">
              Select All
            </button>
            <button onClick={handleClearAll} className="filter-action-btn">
              Clear All
            </button>
          </div>

          <div className="filter-values">
            {filteredValues.length === 0 ? (
              <div className="no-results">No values found</div>
            ) : (
              filteredValues.map((value) => (
                <label key={value} className="filter-value-item">
                  <input
                    type="checkbox"
                    checked={selectedValues.has(value)}
                    onChange={() => handleToggleValue(value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="filter-value-text">{value}</span>
                </label>
              ))
            )}
          </div>

          <div className="filter-footer">
            <button onClick={handleClear} className="filter-btn clear">
              Clear Filter
            </button>
            <button onClick={handleApply} className="filter-btn apply">
              Apply
            </button>
          </div>
        </div>
      </>,
      document.body
    );
  };

  return (
    <div 
      className="column-filter"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        ref={buttonRef}
        className={`filter-trigger ${hasActiveFilter ? 'active' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setIsOpen(!isOpen);
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        title="Filter column"
      >
        <FiFilter size={14} />
        {hasActiveFilter && <span className="filter-badge">{currentFilters.length}</span>}
      </button>
      {renderDropdown()}
    </div>
  );
};

export default ColumnFilter;

